'use client';

import { useEffect } from 'react';

/**
 * Registra el service worker (`/sw.js`) en TODOS los entornos. Aunque
 * la PWA solo es instalable en producción (HTTPS), el SW también debe
 * estar disponible en dev para probar funcionalidades dependientes —
 * en particular las web push notifications, cuyo flujo de subscripción
 * exige una registration activa.
 *
 * Auto-actualización (SUTIL — historial):
 *   - El propio `sw.js` hace `self.skipWaiting()` + `self.clients.claim()`
 *     → cuando hay un SW nuevo, toma control de la pestaña al instante.
 *   - Aquí NO recargamos la pestaña automáticamente en `updatefound`.
 *     Las versiones anteriores sí lo hacían, pero combinado con la
 *     opción "Disable cache" de DevTools (o cualquier proxy que ignore
 *     ETag), el navegador veía `sw.js` como "byte change" en cada F5 →
 *     reinstalación → updatefound → `location.reload()` → register →
 *     reinstalación → BUCLE infinito que saturaba el panel Network
 *     con peticiones fallidas a PRECACHE (`/`, `logo.png`,
 *     `site.webmanifest`) y colgaba el navegador estando offline.
 *
 *     Trade-off: tras un deploy, el usuario sigue viendo la versión
 *     vieja hasta que F5-ee. Aceptable — la app es razonablemente
 *     compatible entre versiones y `clients.claim()` hace que la
 *     próxima navegación ya sirva los chunks nuevos.
 *
 *   - Update check (`registration.update()`) solo se dispara al volver
 *     a la pestaña tras ≥5 min y solo cuando hay red. Sin red,
 *     intentar `.update()` falla silenciosamente pero gasta ciclos.
 */
export default function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    let cancelled = false;

    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        if (cancelled) return;

        // Update check inmediato tras registrar. Sin esto, el cliente
        // se queda con la versión antigua hasta el siguiente cambio
        // de pestaña + debounce de 5 min — y mientras tanto convive
        // con bugs corregidos en versiones nuevas del SW. El propio
        // `skipWaiting+claim` del `sw.js` se encarga de tomar control
        // cuando la nueva versión esté instalada.
        registration.update().catch(() => undefined);

        // Update check al volver visible — solo si online y con
        // debounce de 5 min. NO recargamos ni hacemos nada con el
        // resultado: el SW nuevo se activa con `skipWaiting+claim`
        // y la próxima navegación cliente ya sirve los chunks nuevos.
        let lastUpdateCheck = Date.now();
        const onVisibility = () => {
          if (document.visibilityState !== 'visible') return;
          if (!navigator.onLine) return;
          const now = Date.now();
          if (now - lastUpdateCheck < 5 * 60_000) return;
          lastUpdateCheck = now;
          registration.update().catch(() => undefined);
        };
        document.addEventListener('visibilitychange', onVisibility);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);
  return null;
}
